import { Controller } from "@hotwired/stimulus"

const LINE_COLORS = {
  primary: "#f59e0b",
  secondary: "#1d4ed8"
}

const BAR_COLORS = [ "#f59e0b", "#d97706", "#1d4ed8", "#047857", "#7c3aed" ]

export default class extends Controller {
  static targets = [
    "trafficLine",
    "conversionBars",
    "topReferrersBars",
    "topPlansBars",
    "topViewedPlansBars",
    "topGroupsBars",
    "devicesDonut",
    "landingsBars"
  ]

  static values = {
    trafficSeries: Array,
    participationsSeries: Array,
    conversionSeries: Array,
    topReferrers: Array,
    topPlans: Array,
    topViewedPlans: Array,
    topGroups: Array,
    devices: Array,
    landings: Array
  }

  connect() {
    this.renderTrafficLine()
    this.renderConversionBars()
    this.renderTopReferrers()
    this.renderTopPlans()
    this.renderTopViewedPlans()
    this.renderTopGroups()
    this.renderDevicesDonut()
    this.renderLandingsBars()
  }

  renderTrafficLine() {
    if (!this.hasTrafficLineTarget) return

    const traffic = this.normalizeSeries(this.trafficSeriesValue)
    const participations = this.normalizeSeries(this.participationsSeriesValue)
    if (!traffic.length && !participations.length) {
      this.renderEmpty(this.trafficLineTarget, "Sin datos de tráfico en este rango.")
      return
    }

    const labels = traffic.map((point) => point.label)
    const valuesA = traffic.map((point) => point.value)
    const valuesB = participations.map((point) => point.value)
    const maxValue = Math.max(...valuesA, ...valuesB, 1)

    const width = 560
    const height = 220
    const padX = 28
    const padY = 18
    const chartWidth = width - padX * 2
    const chartHeight = height - padY * 2
    const safeStep = labels.length > 1 ? chartWidth / (labels.length - 1) : 0

    const pointsA = valuesA
      .map((value, index) => {
        const x = padX + safeStep * index
        const y = padY + chartHeight - (value / maxValue) * chartHeight
        return `${x},${y}`
      })
      .join(" ")

    const pointsB = valuesB
      .map((value, index) => {
        const x = padX + safeStep * index
        const y = padY + chartHeight - (value / maxValue) * chartHeight
        return `${x},${y}`
      })
      .join(" ")

    this.trafficLineTarget.innerHTML = `
      <svg viewBox="0 0 ${width} ${height}" class="h-56 w-full" role="img" aria-label="Serie de sesiones y participaciones">
        <line x1="${padX}" y1="${height - padY}" x2="${width - padX}" y2="${height - padY}" stroke="#fde68a" stroke-width="1" />
        <line x1="${padX}" y1="${padY}" x2="${padX}" y2="${height - padY}" stroke="#fde68a" stroke-width="1" />
        <polyline points="${pointsA}" fill="none" stroke="${LINE_COLORS.primary}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />
        <polyline points="${pointsB}" fill="none" stroke="${LINE_COLORS.secondary}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
      <div class="mt-3 flex flex-wrap items-center gap-4 text-xs text-amber-800">
        <span class="inline-flex items-center gap-1.5"><span class="h-2.5 w-2.5 rounded-full" style="background:${LINE_COLORS.primary}"></span>Sesiones</span>
        <span class="inline-flex items-center gap-1.5"><span class="h-2.5 w-2.5 rounded-full" style="background:${LINE_COLORS.secondary}"></span>Participaciones</span>
      </div>
    `
  }

  renderConversionBars() {
    if (!this.hasConversionBarsTarget) return
    const rows = this.normalizeSeries(this.conversionSeriesValue)
    if (!rows.length) {
      this.renderEmpty(this.conversionBarsTarget, "Sin datos de conversión para mostrar.")
      return
    }

    const maxRate = Math.max(...rows.map((row) => row.value), 1)
    const content = rows
      .map((row) => {
        const width = ((row.value / maxRate) * 100).toFixed(1)
        return `
          <div class="space-y-1">
            <div class="flex items-center justify-between text-xs text-amber-800">
              <span>${this.escapeHtml(row.label)}</span>
              <span class="font-semibold">${row.value.toFixed(1)}%</span>
            </div>
            <div class="h-2 rounded-full bg-amber-100">
              <div class="h-2 rounded-full bg-amber-500" style="width:${width}%"></div>
            </div>
          </div>
        `
      })
      .join("")

    this.conversionBarsTarget.innerHTML = `<div class="space-y-3">${content}</div>`
  }

  renderTopReferrers() {
    if (!this.hasTopReferrersBarsTarget) return
    this.renderHorizontalBars(this.topReferrersBarsTarget, this.topReferrersValue, "Sin datos de referidos.")
  }

  renderTopPlans() {
    if (!this.hasTopPlansBarsTarget) return
    const rows = this.topPlansValue.map((row) => ({ label: row.title, value: row.value }))
    this.renderHorizontalBars(this.topPlansBarsTarget, rows, "Sin planes con actividad.")
  }

  renderTopViewedPlans() {
    if (!this.hasTopViewedPlansBarsTarget) return
    const rows = this.topViewedPlansValue.map((row) => ({ label: row.title, value: row.value }))
    this.renderHorizontalBars(this.topViewedPlansBarsTarget, rows, "Sin vistas de planes en este rango.")
  }

  renderTopGroups() {
    if (!this.hasTopGroupsBarsTarget) return
    const rows = this.topGroupsValue.map((row) => ({ label: row.name, value: row.value }))
    this.renderHorizontalBars(this.topGroupsBarsTarget, rows, "Sin grupos con actividad.")
  }

  renderHorizontalBars(target, rows, emptyText) {
    const normalizedRows = this.normalizeSeries(rows)
    if (!normalizedRows.length) {
      this.renderEmpty(target, emptyText)
      return
    }

    const maxValue = Math.max(...normalizedRows.map((row) => row.value), 1)
    const content = normalizedRows
      .map((row, index) => {
        const width = ((row.value / maxValue) * 100).toFixed(1)
        const color = BAR_COLORS[index % BAR_COLORS.length]
        return `
          <div class="space-y-1">
            <div class="flex items-center justify-between gap-3 text-xs text-amber-800">
              <span class="truncate">${this.escapeHtml(row.label)}</span>
              <span class="font-semibold">${this.formatNumber(row.value)}</span>
            </div>
            <div class="h-2 rounded-full bg-amber-100">
              <div class="h-2 rounded-full" style="width:${width}%; background:${color};"></div>
            </div>
          </div>
        `
      })
      .join("")

    target.innerHTML = `<div class="space-y-3">${content}</div>`
  }

  renderDevicesDonut() {
    if (!this.hasDevicesDonutTarget) return
    const rows = this.normalizeSeries(this.devicesValue)
    if (!rows.length) {
      this.renderEmpty(this.devicesDonutTarget, "Sin datos de dispositivos.")
      return
    }

    const total = rows.reduce((acc, row) => acc + row.value, 0)
    if (total <= 0) {
      this.renderEmpty(this.devicesDonutTarget, "Sin datos de dispositivos.")
      return
    }

    const r = 42
    const c = 2 * Math.PI * r
    let offset = 0
    const circles = rows
      .map((row, index) => {
        const color = BAR_COLORS[index % BAR_COLORS.length]
        const dash = (row.value / total) * c
        const node = `
          <circle
            cx="60"
            cy="60"
            r="${r}"
            fill="none"
            stroke="${color}"
            stroke-width="14"
            stroke-dasharray="${dash} ${c - dash}"
            stroke-dashoffset="${-offset}"
            transform="rotate(-90 60 60)"
          />
        `
        offset += dash
        return node
      })
      .join("")

    const legend = rows
      .map((row, index) => {
        const color = BAR_COLORS[index % BAR_COLORS.length]
        const label = this.escapeHtml(row.label)
        return `
          <div class="flex items-center justify-between text-xs text-amber-800">
            <span class="inline-flex items-center gap-2 truncate">
              <span class="h-2.5 w-2.5 rounded-full" style="background:${color}"></span>${label}
            </span>
            <span class="font-semibold">${this.formatNumber(row.value)}</span>
          </div>
        `
      })
      .join("")

    this.devicesDonutTarget.innerHTML = `
      <div class="flex flex-col gap-4 sm:flex-row sm:items-center">
        <svg viewBox="0 0 120 120" class="h-36 w-36 shrink-0">
          <circle cx="60" cy="60" r="${r}" fill="none" stroke="#fef3c7" stroke-width="14"></circle>
          ${circles}
          <text x="60" y="58" text-anchor="middle" class="fill-amber-900 text-[14px] font-semibold">${this.formatNumber(total)}</text>
          <text x="60" y="73" text-anchor="middle" class="fill-amber-700 text-[10px]">sesiones</text>
        </svg>
        <div class="flex-1 space-y-2">${legend}</div>
      </div>
    `
  }

  renderLandingsBars() {
    if (!this.hasLandingsBarsTarget) return
    const rows = this.landingsValue.map((row) => ({
      label: `/${row.slug}`,
      value: Number(row.conversion_rate) || 0,
      visits: Number(row.visits) || 0,
      ctaClicks: Number(row.cta_clicks) || 0
    }))

    if (!rows.length) {
      this.renderEmpty(this.landingsBarsTarget, "Aún no hay datos de landings en este rango.")
      return
    }

    const content = rows
      .map((row) => {
        return `
          <div class="space-y-1">
            <div class="flex flex-wrap items-center justify-between gap-2 text-xs text-amber-800">
              <span class="font-medium">${this.escapeHtml(row.label)}</span>
              <span><strong>${this.formatNumber(row.visits)}</strong> visitas · <strong>${this.formatNumber(row.ctaClicks)}</strong> CTA</span>
              <span class="font-semibold">${row.value.toFixed(1)}%</span>
            </div>
            <div class="h-2 rounded-full bg-amber-100">
              <div class="h-2 rounded-full bg-amber-500" style="width:${Math.min(row.value, 100)}%"></div>
            </div>
          </div>
        `
      })
      .join("")

    this.landingsBarsTarget.innerHTML = `<div class="space-y-3">${content}</div>`
  }

  normalizeSeries(series) {
    if (!Array.isArray(series)) return []

    return series
      .map((row) => {
        const label = row.label || row.name || row.slug || "N/A"
        const rawValue = row.value ?? row.rate ?? row.conversion_rate
        return {
          label: String(label),
          value: Number(rawValue) || 0
        }
      })
      .filter((row) => Number.isFinite(row.value))
  }

  renderEmpty(target, text) {
    target.innerHTML = `<p class="rounded-xl border border-amber-100 bg-amber-50/60 p-3 text-sm text-amber-700">${this.escapeHtml(text)}</p>`
  }

  formatNumber(value) {
    return new Intl.NumberFormat("es-ES").format(value)
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;")
  }
}
